"use client";

import { format } from "date-fns";
import { motion } from "framer-motion";
import { Calendar, Clock, Newspaper } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useBlogStore } from "@/lib/store/blog-store";

interface RecentlyViewedBlogsProps {
	currentArticleId: string;
}

const container = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: {
			staggerChildren: 0.1,
			delayChildren: 0.1,
		},
	},
};

const item = {
	hidden: { opacity: 0, y: 30 },
	show: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.5, ease: "easeOut" as const },
	},
};

export function RecentlyViewedBlogs({
	currentArticleId,
}: RecentlyViewedBlogsProps) {
	const recentlyViewed = useBlogStore((s) => s.recentlyViewed);

	const filtered = recentlyViewed
		.filter((a) => a.id !== currentArticleId)
		.slice(0, 4);

	if (filtered.length === 0) return null;

	return (
		<section className="mt-12 pt-10">
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, margin: "-100px" }}
				transition={{ duration: 0.5 }}
				className="flex items-center gap-2 mb-6"
			>
				<Clock className="h-5 w-5 text-muted-foreground" />
				<h2 className="text-2xl font-bold">Recently Viewed</h2>
			</motion.div>

			<motion.div
				variants={container}
				initial="hidden"
				whileInView="show"
				viewport={{ once: true, margin: "-50px" }}
				className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
			>
				{filtered.map((article) => (
					<motion.div key={article.id} variants={item}>
						<Link
							href={`/blog/${article.handle}`}
							className="block group h-full"
						>
							<Card className="h-full overflow-hidden transition-all hover:shadow-lg hover:border-primary/50 hover:-translate-y-1 duration-300 border shadow-sm p-0 gap-0">
								<div className="relative aspect-video overflow-hidden">
									{article.image ? (
										<Image
											src={article.image.url}
											alt={article.image.altText || article.title}
											fill
											className="object-cover transition-transform duration-500 group-hover:scale-105"
											sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
										/>
									) : (
										<div className="h-full bg-secondary flex items-center justify-center">
											<Newspaper className="h-8 w-8 text-muted-foreground" />
										</div>
									)}
								</div>
								<CardContent className="p-4">
									{article.blog?.title && (
										<Badge variant="secondary" className="mb-1.5 text-xs">
											{article.blog.title}
										</Badge>
									)}
									<h3 className="font-semibold text-sm group-hover:text-primary transition-colors line-clamp-2 min-h-10">
										{article.title}
									</h3>
									<div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
										<Calendar className="h-3 w-3" />
										<span>
											{format(new Date(article.publishedAt), "MMM d, yyyy")}
										</span>
									</div>
								</CardContent>
							</Card>
						</Link>
					</motion.div>
				))}
			</motion.div>
		</section>
	);
}
